import { createTransform } from "redux-persist";
import { encryptData, decryptData } from "./encryption";
import { getParsedData, scheduleWrite, type StorageState } from "./storageHelpers";

// A custom redux-persist storage engine to bundle all persisted keys into a single dynamic key in localStorage.
export const createGroupedStorage = (storageKey: string) => {
    const state: StorageState = { memoryCache: null, writeTimeoutId: null };

    return {
        getItem: (key: string): Promise<string | null> => {
            return new Promise((resolve) => {
                try {
                    const parsed = getParsedData(state, storageKey);
                    if (!parsed) return resolve(null);

                    const val = parsed[key];

                    if (val === undefined || val === null) {
                        return resolve(null);
                    }
                    if (typeof val === "object") {
                        resolve(JSON.stringify(val));
                    } else {
                        resolve(String(val));
                    }
                } catch (e) {
                    resolve(null);
                }
            });
        },
        setItem: (key: string, value: string): Promise<void> => {
            return new Promise((resolve) => {
                const parsed = getParsedData(state, storageKey);
                if (parsed) {
                    try {
                        parsed[key] = JSON.parse(value);
                    } catch (e) {
                        parsed[key] = value;
                    }
                    scheduleWrite(state, storageKey);
                }
                resolve();
            });
        },
        removeItem: (key: string): Promise<void> => {
            return new Promise((resolve) => {
                const parsed = getParsedData(state, storageKey);
                if (parsed) {
                    delete parsed[key];
                    scheduleWrite(state, storageKey);
                }
                resolve();
            });
        },
    };
};

export const createEncryptTransform = (secretKey: string) => {
    return createTransform(
        (inboundState) => {
            if (!inboundState) return inboundState;
            if (!secretKey) {
                // Return plain text state if key is missing
                return inboundState;
            }
            try {
                const encrypted = encryptData(inboundState, secretKey);
                if (!encrypted) {
                    throw new Error("Encryption failed: encryptData returned null. Refusing to persist unencrypted state.");
                }
                return { _encrypted: encrypted };
            } catch (error) {
                console.error("Encrypt transform error:", error);
                throw error;
            }
        },
        (outboundState) => {
            if (!outboundState) return outboundState;
            try {
                if (
                    typeof outboundState === "object" &&
                    outboundState !== null &&
                    "_encrypted" in outboundState
                ) {
                    const encryptedValue = (outboundState as { _encrypted: string })._encrypted;
                    if (typeof encryptedValue !== "string") {
                        return {};
                    }
                    if (!secretKey) {
                        return {}; // Cannot decrypt without key
                    }
                    const decrypted = decryptData(encryptedValue, secretKey);
                    return decrypted ?? {};
                }
                return outboundState;
            } catch (error) {
                console.error("Decrypt transform error:", error);
                return outboundState;
            }
        }
    );
};
